import {
  cloneElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { createPortal } from 'react-dom'

import { Box, Text, VStack, chakra } from '@enechain/ecloud-designsystem'

import { iconSize } from '~/constants/iconSize'
import { useDropdownDismiss } from '~/hooks/useDropdownDismiss'

export type RichActionMenuItem = {
  /** 項目の識別子 (key)。 */
  value: string
  /** 1 行目。 accessible name にもこれを使う (説明文は読み上げに混ぜない)。 */
  title: string
  /** 2 行目の説明文。 */
  description: string
  icon: React.ReactNode
  onClick: () => void
  /**
   * 選べない項目 (= 条件が揃っていない作成方法)。 menu から消さずに残すのは、
   * 「その作成方法が存在すること」 自体は知らせたいため (消すと選択肢が
   * 画面ごとに増減して見え、 何が出来るのかが読めなくなる)。
   */
  disabled?: boolean
}

/** trigger と menu の間隔 px。 */
const MENU_GAP = 4

type PlacementInput = {
  /** trigger の上端 (viewport 座標)。 */
  triggerTop: number
  /** trigger の下端 (viewport 座標)。 */
  triggerBottom: number
  viewportHeight: number
  menuHeight: number
  gap: number
}

/**
 * menu を trigger の下に開くか上に開くか。
 *
 * design/ui-feasibility/components/split-button.md §1「上下の向き」 (2026-10-05 デザイナー判断):
 * **下に開くのが既定**。 下に収まらず、 **上の方が広い時だけ上に開く**
 * (上下とも収まらない時も広い方)。 trigger の右 (横) には出さない。
 * DS `Menu` は zag popper の既定 (`flip: true`) で同じ動きをするので、 自前の本 component も揃える。
 */
export const resolveMenuPlacement = ({
  triggerTop,
  triggerBottom,
  viewportHeight,
  menuHeight,
  gap,
}: PlacementInput): 'bottom' | 'top' => {
  const spaceBelow = viewportHeight - triggerBottom - gap
  if (menuHeight <= spaceBelow) return 'bottom'
  const spaceAbove = triggerTop - gap
  return spaceAbove > spaceBelow ? 'top' : 'bottom'
}

type Props = {
  /** 開閉の trigger。 `onClick` は本 component が差し込む。 */
  trigger: React.ReactElement<{
    onClick?: () => void
    'aria-haspopup'?: string
    'aria-expanded'?: boolean
  }>
  items: RichActionMenuItem[]
  /** menu の accessible name。 */
  ariaLabel: string
}

/**
 * 「アイコン + タイトル + 説明文」 の 2 行項目を持つ action menu。
 *
 * design 8080 の `.ecl-menu-item--rich`
 * (`bidding/spot.html` の 入札追加 split button の menu) 相当。
 *
 * ## [DS gap] なぜ DS `Menu` を使わないか
 *
 * DS `Menu` の `content[].label` は **`string` 固定**で `ReactNode` を受けない。
 * そのため「タイトル + 説明文」 の 2 行項目を DS の API では作れない
 * (`icon` は `ReactNode` を受けるが、 それはアイコン枠であって本文ではない)。
 *
 * Chakra v3 の `Menu` namespace primitive は型定義が react-19 前提で本 app
 * (react-18) と噛み合わない (`FilterMultiSelect` / `MultiSelect` と同じ制約)。
 * よって**同じ土台 (Box/Flex + `useDropdownDismiss`) で自前に組む**。
 * 2026-08-24 デザイナー判断で自前実装を選択。
 *
 * **DS `Menu` が ReactNode ラベルを受けるようになったら、 この component を消して
 * DS へ寄せること。**
 *
 * ## a11y
 *
 * 項目の accessible name は **タイトルだけ** (`aria-label`)。 説明文まで読み上げると
 * 「一括作成 調達必要量の指定%まで…」 と長くなり、 選択肢の聞き分けが悪くなるため
 * (説明文は視覚的な補助として残す)。
 *
 * `role="menu"` / `role="menuitem"` を名乗る以上、 **menu button パターンの
 * キーボード操作を伴わせる** (置き換え元の DS `Menu` が持っていたため、 無いと退行に
 * なる)。 実装しているのは次の 5 つ:
 *
 * - trigger に `aria-haspopup="menu"` / `aria-expanded` で開閉状態を出す
 * - trigger 上の `ArrowDown` / `ArrowUp` で開き、 それぞれ先頭 / 末尾へ focus
 * - 開いたら項目へ focus を移す (menu は開いた時点で項目が操作対象になる)
 * - 項目間は `ArrowDown` / `ArrowUp` (端で循環) / `Home` / `End`
 * - `Escape` で閉じて trigger へ focus を戻す
 *
 * 項目の `tabIndex` は roving (focus 中の 1 つだけ 0)。 menu の中を Tab で
 * 送らせず、 Tab は menu の外へ抜ける = menu button パターンの既定。
 *
 * ## popup は body へ portal する
 *
 * menu は **`document.body` へ portal** し `position: fixed` で trigger の rect から
 * 位置を出す。 trigger の子として `position: absolute` に置くと、 スクロールする
 * 祖先 (ツールバーを含む `overflow: auto` の表 wrap 等) にクリップされて項目が
 * 見切れる (2026-08-25 デザイナー指摘: 時間前入札の「入札追加」で menu が切れた)。
 * [[CompactCivilDateField]] / DS `Select` (`menuPortalTarget={document.body}` +
 * `zIndex: 1500`) と同じ解き方に揃える。
 *
 * ## 上下の向き
 *
 * 開くたび (とスクロール / リサイズのたび) に trigger の上下の空きと menu の高さを測り、
 * 下に収まらず上の方が広い時だけ `bottom` 基準で上に開く ([[resolveMenuPlacement]])。
 * 以前は常に `top = trigger の下端 + gap` で、 画面下端の trigger では menu が画面外へ出ていた。
 */
export const RichActionMenu = ({
  trigger,
  items,
  ariaLabel,
}: Props): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  /** focus を当てる項目の index。 閉じている間の値は次に開いた時の初期位置。 */
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  /**
   * portal 先での menu 位置 (viewport 座標)。 trigger の右端に右揃えする。
   * 下に開く時は `top`、 上に開く時は `bottom` (viewport 下端からの距離) だけを持つ。
   */
  const [position, setPosition] = useState<{
    top?: number
    bottom?: number
    right: number
  }>({ top: 0, right: 0 })

  // portal した menu は `rootRef` の subtree 外なので `menuRef` を渡す。
  // 渡さないと項目クリックが outside click と判定され、 click 前に unmount される。
  useDropdownDismiss(
    rootRef,
    isOpen,
    () => {
      setIsOpen(false)
    },
    menuRef,
  )

  const updatePosition = useCallback((): void => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (rect === undefined) return
    // menu は open と同じ commit で描画済みなので、 layout effect の時点で高さを測れる
    // (位置は高さに影響しない)。
    const placement = resolveMenuPlacement({
      triggerTop: rect.top,
      triggerBottom: rect.bottom,
      viewportHeight: window.innerHeight,
      menuHeight: menuRef.current?.getBoundingClientRect().height ?? 0,
      gap: MENU_GAP,
    })
    // `right` は viewport 右端からの距離。 trigger の右端に揃える。
    const right = Math.max(0, window.innerWidth - rect.right)
    setPosition(
      placement === 'top'
        ? { bottom: window.innerHeight - rect.top + MENU_GAP, right }
        : { top: rect.bottom + MENU_GAP, right },
    )
  }, [])

  useLayoutEffect(() => {
    if (isOpen) updatePosition()
  }, [isOpen, updatePosition])

  // 開いている間はスクロール / リサイズに追随させる (fixed は祖先の
  // スクロールで自動追従しないため)。 scroll は capture 相で拾って
  // 祖先のスクロールコンテナも拾う。
  useEffect(() => {
    if (!isOpen) return undefined
    const handleReposition = (): void => {
      updatePosition()
    }
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, updatePosition])

  /** 閉じた後に trigger へ focus を戻すか (Escape で閉じた時だけ true)。 */
  const returnFocusRef = useRef(false)

  // 開いている間は activeIndex の項目に focus を保つ (roving tabindex の実体)。
  // 閉じた直後は必要なら trigger へ戻す。 close() の中で同期に focus すると
  // まだ menu が DOM に残っており、 直後の再 render で focus が body へ落ちるため、
  // DOM 更新後に走る effect で行う。
  useEffect(() => {
    if (isOpen) {
      itemRefs.current[activeIndex]?.focus()
      return
    }
    if (!returnFocusRef.current) return
    returnFocusRef.current = false
    // menu は閉じた後なので、 root 直下に残る button は trigger だけ。
    rootRef.current?.querySelector('button')?.focus()
  }, [isOpen, activeIndex])

  // Escape は [[useDropdownDismiss]] が **window の capture 相**で拾って
  // `stopPropagation` するため (親 modal ごと閉じるのを防ぐ設計)、 menu 側の
  // `onKeyDown` までは届かない。 閉じる処理は hook に任せ、 ここでは「Escape で
  // 閉じた」 ことだけを拾って focus 戻しの要否を立てる。 outside click で閉じた
  // 時は戻さない (= クリック先から focus を奪わない)。
  useEffect(() => {
    if (!isOpen) return undefined
    const markEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') returnFocusRef.current = true
    }
    window.addEventListener('keydown', markEscape, true)
    return () => {
      window.removeEventListener('keydown', markEscape, true)
    }
  }, [isOpen])

  const close = (returnFocus: boolean): void => {
    returnFocusRef.current = returnFocus
    setIsOpen(false)
  }

  const openAt = (index: number): void => {
    setActiveIndex(index)
    setIsOpen(true)
  }

  return (
    <Box ref={rootRef} position="relative" display="inline-flex">
      <chakra.span
        display="inline-flex"
        onClick={() => {
          if (isOpen) {
            setIsOpen(false)
            return
          }
          openAt(0)
        }}
        // Enter / Space は trigger が button なので click に落ちる。 ここで拾うのは
        // 「閉じた menu を矢印で開く」 分だけ。
        onKeyDown={(e) => {
          if (isOpen) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            openAt(0)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            openAt(items.length - 1)
          }
        }}
      >
        {cloneElement(trigger, {
          'aria-expanded': isOpen,
          'aria-haspopup': 'menu',
        })}
      </chakra.span>
      {isOpen &&
        createPortal(
          <VStack
            ref={menuRef}
            position="fixed"
            zIndex={1500}
            top={position.top === undefined ? undefined : `${position.top}px`}
            right={`${position.right}px`}
            bottom={
              position.bottom === undefined ? undefined : `${position.bottom}px`
            }
            align="stretch"
            gap={0}
            // UI 辞書 components/menu.md「DS recipe 値」: 面は DS Menu と同じ
            // (上下 8px・左右 0 / 角丸 radiiMd 6px / 極薄の影 shadSm)。 2 行 item を含む
            // メニューの最小幅は 264px ([DS差異] 説明が 3 行に割れないため。 `.ecl-menu:has(.ecl-menu-item--rich)`)。
            // 幅は 264px に揃え、 説明は折り返す。 8080 の `.ecl-menu` は absolute 配置の
            // shrink-to-fit で max(min-content, 264px) になる。 こちらは fixed + `right` で
            // 横の空きが広く、 既定の幅だと説明 1 行ぶん (実測 372px) まで広がって 8080 と割れる
            // (2026-10-19 実測)。 `min-content` + 最小幅で 8080 と同じ決まり方にする
            width="min-content"
            minWidth="264px"
            borderWidth="1px"
            borderColor="Border.lowEmphasis"
            borderRadius="md"
            boxShadow="shadSm"
            aria-label={ariaLabel}
            backgroundColor="Surface.primary"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActiveIndex((prev) => (prev + 1) % items.length)
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActiveIndex(
                  (prev) => (prev - 1 + items.length) % items.length,
                )
              } else if (e.key === 'Home') {
                e.preventDefault()
                setActiveIndex(0)
              } else if (e.key === 'End') {
                e.preventDefault()
                setActiveIndex(items.length - 1)
              }
              // Escape はここには来ない (上記 useEffect のコメント参照)。
            }}
            paddingX={0}
            paddingY={2}
            role="menu"
          >
            {items.map((item, index) => (
              <chakra.button
                key={item.value}
                ref={(el: HTMLButtonElement | null) => {
                  itemRefs.current[index] = el
                }}
                alignItems="flex-start"
                // menu.md / 8080 `.ecl-menu-item`: icon と本文の間 8px (`gap: var(--space-2)`)。
                // 角丸は付けない (DS `menu.recipe` の item は `borderRadius: 0`)
                gap={2}
                display="flex"
                width="100%"
                padding="8px 12px"
                textAlign="left"
                opacity={item.disabled === true ? 0.5 : undefined}
                _hover={{ backgroundColor: 'Hover.lowEmphasis' }}
                // menu.md「item pressed = Press.lowEmphasis / item focus = focus ring」
                _active={{ backgroundColor: 'Press.lowEmphasis' }}
                _focusVisible={{
                  outline: 'none',
                  boxShadow: 'inset 0 0 0 3px {colors.Focus.clarity}',
                }}
                cursor={item.disabled === true ? 'not-allowed' : 'pointer'}
                aria-disabled={item.disabled}
                // native `disabled` ではなく `aria-disabled` にする。 `role="menuitem"`
                // は矢印キーで**無効項目にも移動できる**のが WAI-ARIA の作法で、
                // native disabled だと focus 自体が当たらず飛ばされてしまう。
                aria-label={item.title}
                onClick={() => {
                  if (item.disabled === true) return
                  close(false)
                  item.onClick()
                }}
                role="menuitem"
                tabIndex={index === activeIndex ? 0 : -1}
                type="button"
              >
                {/* icon は 20px 固定 (`iconSize.menuItem`。 呼び出し側の boxSize に依らず揃える)。
                    色は持たない — 文字色 (currentColor) に従う (menu.md「色は文字に当てる。
                    アイコンには当てない」) */}
                <chakra.span
                  flexShrink={0}
                  display="inline-flex"
                  marginTop="2px"
                  aria-hidden
                  css={{
                    '& svg': {
                      width: iconSize.menuItem,
                      height: iconSize.menuItem,
                    },
                  }}
                >
                  {item.icon}
                </chakra.span>
                {/* タイトルと説明の間 2px (8080 `.ecl-menu-item-body { gap: 2px }`) */}
                <VStack align="stretch" gap={0.5}>
                  <Text textStyle="label" color="Text.highEmphasis">
                    {item.title}
                  </Text>
                  {/* menu.md 2 行 item の desc = xs / Text.midEmphasis / 400 (`.ecl-menu-item-desc`) */}
                  <Text textStyle="menuItemDesc" color="Text.midEmphasis">
                    {item.description}
                  </Text>
                </VStack>
              </chakra.button>
            ))}
          </VStack>,
          document.body,
        )}
    </Box>
  )
}
